import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Button,
  Icon,
  Flex,
  Select,
  Spinner,
  SimpleGrid,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { FiBarChart2, FiClock, FiCalendar, FiActivity, FiLogOut, FiUsers, FiCamera, FiMap } from 'react-icons/fi';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import {
  obtenerEstadisticasActividad,
  obtenerDenunciasPorZona
} from '../config/index';
import type {
  EstadisticasPorSemana,
  EstadisticasPorDia,
  EstadisticasPorHora,
  UsuarioActivo,
  TiempoRespuesta,
  EstadisticasTipoBarrera
} from '../config/index';
import MapaZonasDenuncias, { DenunciaPorZona } from '../components/MapaZonasDenuncias';

export const EstadisticasActividad: React.FC = () => {
  const { currentUser } = useAuth();
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState<number>(6); // 6 meses por defecto
  const [cargando, setCargando] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [estadisticas, setEstadisticas] = useState<{
    porSemana: EstadisticasPorSemana[];
    porDia: EstadisticasPorDia[];
    porHora: EstadisticasPorHora[];
    usuariosMasActivos: UsuarioActivo[];
    porcentajeDenunciasConFoto: number;
    tiempoRespuestaInicial: TiempoRespuesta;
    tiposBarreras: EstadisticasTipoBarrera[];
  } | null>(null);
  const [denunciasPorZona, setDenunciasPorZona] = useState<DenunciaPorZona[]>([]);
  const [cargandoMapa, setCargandoMapa] = useState<boolean>(true);
  
  const navigate = useNavigate();
  const brandRed = useColorModeValue('red.500', 'red.300');
  
  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true);
      setCargandoMapa(true);
      setError(null);
      
      try {
        console.log('Iniciando carga de estadísticas optimizada...');
        
        // Cargar estadísticas primero (más rápido con caché)
        const estadisticasPromise = obtenerEstadisticasActividad(periodoSeleccionado);
        
        // Cargar denuncias por zona de forma independiente
        const denunciasPromise = obtenerDenunciasPorZona(periodoSeleccionado);
        
        // Timeout más largo para dar tiempo a las optimizaciones
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout: La carga de datos está tardando demasiado')), 60000)
        );
        
        // Cargar estadísticas primero
        const datos = await Promise.race([
          estadisticasPromise,
          timeoutPromise
        ]) as {
          porSemana: EstadisticasPorSemana[];
          porDia: EstadisticasPorDia[];
          porHora: EstadisticasPorHora[];
          usuariosMasActivos: UsuarioActivo[];
          porcentajeDenunciasConFoto: number;
          tiempoRespuestaInicial: TiempoRespuesta;
          tiposBarreras: EstadisticasTipoBarrera[];
        };
        
        console.log('Estadísticas cargadas exitosamente');
        setEstadisticas(datos);
        setCargando(false);
        
        // Cargar denuncias por zona de forma asíncrona
        try {
          const denuncias = await denunciasPromise;
          console.log('Denuncias por zona cargadas exitosamente');
          setDenunciasPorZona(denuncias);
        } catch (denunciasError) {
          console.error('Error al cargar denuncias por zona:', denunciasError);
          setDenunciasPorZona([]);
        } finally {
          setCargandoMapa(false);
        }
        
      } catch (error) {
        console.error('Error al cargar estadísticas:', error);
        setError(error instanceof Error ? error.message : 'Error desconocido al cargar los datos');
        
        // Establecer datos vacíos para mostrar la interfaz
        setEstadisticas({
          porSemana: [],
          porDia: [],
          porHora: [],
          usuariosMasActivos: [],
          porcentajeDenunciasConFoto: 0,
          tiempoRespuestaInicial: {
            tiempoPromedio: 0,
            expedientesConRespuesta: 0,
            expedientesSinRespuesta: 0
          },
          tiposBarreras: []
        });
        setDenunciasPorZona([]);
        setCargando(false);
        setCargandoMapa(false);
      }
    };
    
    cargarDatos();
  }, [periodoSeleccionado]);
  
  // Función para obtener el color basado en la cantidad (para gráficos)
  const obtenerColorIntensidad = (cantidad: number, max: number): string => {
    const intensidad = Math.min(Math.max(cantidad / max, 0.1), 1);
    return `rgba(229, 62, 62, ${intensidad})`; // Rojo con opacidad variable
  };

  // Formatea clave "2025-S52" a "S52 · 2025" para mayor legibilidad
  const formatearSemana = (clave: string): string => {
    const match = clave.match(/^(\d{4})-S(\d+)$/);
    if (match) return `S${match[2]} · ${match[1]}`;
    return clave;
  };
  
  // Componente para el gráfico de barras por semana
  const GraficoPorSemana = () => {
    if (!estadisticas?.porSemana.length) {
      return <Text textAlign="center">No hay datos disponibles</Text>;
    }
    
    const maxCantidad = Math.max(...estadisticas.porSemana.map(item => item.cantidad));
    const totalDenuncias = estadisticas.porSemana.reduce((sum, item) => sum + item.cantidad, 0);
    
    return (
      <Box
        p={4}
        pb={10}
        bg="white"
        borderRadius="md"
        boxShadow="sm"
        overflowX="auto"
        minHeight="320px"
      >
        <Heading size="md" mb={1}>Denuncias por Semana</Heading>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Cada barra: número de denuncias en esa semana (año-Sn).
        </Text>
        <Flex
          minWidth="max-content"
          alignItems="flex-end"
          gap={3}
          pb={2}
          height="280px"
        >
          {estadisticas.porSemana.map((item, index) => (
            <VStack
              key={index}
              flexShrink={0}
              width="56px"
              minW="48px"
              maxW="64px"
              align="center"
              spacing={1}
              height="100%"
              justify="flex-end"
            >
              <Text fontWeight="bold" fontSize="sm" mb={0}>
                {item.cantidad}
              </Text>
              <Box
                width="100%"
                height={`${maxCantidad > 0 ? (item.cantidad / maxCantidad) * 220 : 0}px`}
                minHeight={item.cantidad > 0 ? '20px' : '0'}
                bg={obtenerColorIntensidad(item.cantidad, maxCantidad)}
                borderRadius="md"
                _hover={{
                  opacity: 0.8,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.2s'
                }}
              />
              <Text
                fontSize="xs"
                fontWeight="medium"
                textAlign="center"
                noOfLines={2}
                title={item.semana}
              >
                {formatearSemana(item.semana)}
              </Text>
            </VStack>
          ))}
        </Flex>
        {estadisticas.porSemana.length > 0 && (
          <Text fontSize="xs" color="gray.500" mt={2}>
            Total: {totalDenuncias} denuncias en {estadisticas.porSemana.length} semana{estadisticas.porSemana.length !== 1 ? 's' : ''}.
          </Text>
        )}
      </Box>
    );
  };
  
  // Componente para el gráfico de barras por día de la semana
  const GraficoPorDia = () => {
    if (!estadisticas?.porDia.length) {
      return <Text textAlign="center">No hay datos disponibles</Text>;
    }
    
    const maxCantidad = Math.max(...estadisticas.porDia.map(item => item.cantidad));
    
    return (
      <Box p={4} bg="white" borderRadius="md" boxShadow="sm">
        <Heading size="md" mb={4}>Denuncias por Día de la Semana</Heading>
        <Flex height="300px" alignItems="flex-end" gap={4} justifyContent="space-between">
          {estadisticas.porDia.map((item, index) => (
            <Box key={index} position="relative" width="100%">
              <Box
                height={`${(item.cantidad / maxCantidad) * 250}px`}
                minHeight="20px"
                bg={obtenerColorIntensidad(item.cantidad, maxCantidad)}
                borderRadius="md"
                position="relative"
                _hover={{
                  opacity: 0.8,
                  transform: 'translateY(-5px)',
                  transition: 'all 0.3s'
                }}
              >
                <Text
                  position="absolute"
                  top="-25px"
                  left="50%"
                  transform="translateX(-50%)"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  {item.cantidad}
                </Text>
              </Box>
              <Text
                mt={2}
                fontSize="sm"
                fontWeight="medium"
                textAlign="center"
              >
                {item.dia.substring(0, 3)}
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>
    );
  };
  
  // Componente para el gráfico de barras por hora del día
  const GraficoPorHora = () => {
    if (!estadisticas?.porHora.length) {
      return <Text textAlign="center">No hay datos disponibles</Text>;
    }
    
    const maxCantidad = Math.max(...estadisticas.porHora.map(item => item.cantidad));
    
    return (
      <Box p={4} bg="white" borderRadius="md" boxShadow="sm" overflowX="auto">
        <Heading size="md" mb={4}>Denuncias por Hora del Día</Heading>
        <Flex minWidth="max-content" height="300px" alignItems="flex-end" gap={1}>
          {estadisticas.porHora.map((item, index) => (
            <Box key={index} position="relative" width="100%" maxW="30px" minW="20px">
              <Box
                height={`${(item.cantidad / maxCantidad) * 250}px`}
                minHeight="20px"
                bg={obtenerColorIntensidad(item.cantidad, maxCantidad)}
                borderRadius="md"
                position="relative"
                _hover={{
                  opacity: 0.8,
                  transform: 'translateY(-5px)',
                  transition: 'all 0.3s'
                }}
              >
                <Text
                  position="absolute"
                  top="-25px"
                  left="50%"
                  transform="translateX(-50%)"
                  fontWeight="bold"
                  fontSize="xs"
                >
                  {item.cantidad}
                </Text>
              </Box>
              <Text
                mt={2}
                fontSize="xs"
                fontWeight="medium"
                textAlign="center"
              >
                {item.hora}h
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>
    );
  };
  
  // Componente para el gráfico de usuarios más activos
  const GraficoUsuariosMasActivos = () => {
    if (!estadisticas?.usuariosMasActivos.length) {
      return <Text textAlign="center">No hay datos disponibles</Text>;
    }
    
    const maxActividades = Math.max(...estadisticas.usuariosMasActivos.map(item => item.actividades));
    
    return (
      <Box p={4} bg="white" borderRadius="md" boxShadow="sm">
        <Heading size="md" mb={4}>Usuarios Más Activos</Heading>
        <Box overflowX="auto" pb={4}>
          <Flex minWidth="max-content" height="300px" alignItems="flex-end" gap={3}>
            {estadisticas.usuariosMasActivos.map((item, index) => (
              <Box key={index} position="relative" minW="80px" maxW="120px" flex="0 0 auto">
                <Box
                  height={`${(item.actividades / maxActividades) * 250}px`}
                  minHeight="20px"
                  bg={obtenerColorIntensidad(item.actividades, maxActividades)}
                  borderRadius="md"
                  position="relative"
                  _hover={{
                    opacity: 0.8,
                    transform: 'translateY(-5px)',
                    transition: 'all 0.3s'
                  }}
                >
                  <Text
                    position="absolute"
                    top="-25px"
                    left="50%"
                    transform="translateX(-50%)"
                    fontWeight="bold"
                    fontSize="sm"
                    whiteSpace="nowrap"
                  >
                    {item.actividades}
                  </Text>
                </Box>
                <Text
                  mt={2}
                  fontSize="xs"
                  fontWeight="medium"
                  textAlign="center"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  lineHeight="1.2"
                >
                  {item.usuario}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
        <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
          {estadisticas.usuariosMasActivos.length > 5 && 'Desplaza horizontalmente para ver todos los usuarios'}
        </Text>
      </Box>
    );
  };
  
  // Componente para mostrar estadísticas resumidas
  const ResumenEstadisticas = () => {
    if (!estadisticas) return null;
    
    // Encontrar el día con más denuncias
    const diaMasActivo = [...estadisticas.porDia].sort((a, b) => b.cantidad - a.cantidad)[0];
    
    // Encontrar la hora con más denuncias
    const horaMasActiva = [...estadisticas.porHora].sort((a, b) => b.cantidad - a.cantidad)[0];
    
    // Encontrar el usuario más activo
    const usuarioMasActivo = estadisticas.usuariosMasActivos.length > 0 ? 
      estadisticas.usuariosMasActivos[0] : null;
    
    // Calcular el total de denuncias
    const totalDenuncias = estadisticas.porDia.reduce((sum, item) => sum + item.cantidad, 0);
    
    // Obtener los tipos de barreras principales
    const tiposBarreras = estadisticas.tiposBarreras || [];
    
    return (
      <>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
          <Box p={4} bg="red.50" borderRadius="lg" boxShadow="sm">
            <HStack spacing={3}>
              <Icon as={FiCalendar} boxSize={10} color="red.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.600">Día más activo</Text>
                <Heading size="lg">{diaMasActivo?.dia || 'N/A'}</Heading>
                <Text fontSize="xs" color="gray.500">
                  {diaMasActivo ? `${diaMasActivo.cantidad} denuncias` : 'Sin datos'}
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box p={4} bg="orange.50" borderRadius="lg" boxShadow="sm">
            <HStack spacing={3}>
              <Icon as={FiClock} boxSize={10} color="orange.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.600">Hora más activa</Text>
                <Heading size="lg">{horaMasActiva ? `${horaMasActiva.hora}:00` : 'N/A'}</Heading>
                <Text fontSize="xs" color="gray.500">
                  {horaMasActiva ? `${horaMasActiva.cantidad} denuncias` : 'Sin datos'}
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box p={4} bg="blue.50" borderRadius="lg" boxShadow="sm">
            <HStack spacing={3}>
              <Icon as={FiActivity} boxSize={10} color="blue.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.600">Total en el período</Text>
                <Heading size="lg">{totalDenuncias}</Heading>
                <Text fontSize="xs" color="gray.500">
                  Últimos {periodoSeleccionado} meses
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box p={4} bg="green.50" borderRadius="lg" boxShadow="sm">
            <HStack spacing={3}>
              <Icon as={FiUsers} boxSize={10} color="green.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.600">Usuario más activo</Text>
                <Heading size="lg" fontSize="xl" isTruncated maxW="200px">
                  {usuarioMasActivo?.usuario || 'N/A'}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  {usuarioMasActivo ? `${usuarioMasActivo.actividades} actividades` : 'Sin datos'}
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box p={4} bg="purple.50" borderRadius="lg" boxShadow="sm">
            <HStack spacing={3}>
              <Icon as={FiCamera} boxSize={10} color="purple.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.600">Denuncias con fotografía</Text>
                <Heading size="lg">{estadisticas.porcentajeDenunciasConFoto}%</Heading>
                <Text fontSize="xs" color="gray.500">
                  Del total de denuncias
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box p={4} bg="teal.50" borderRadius="lg" boxShadow="sm">
            <HStack spacing={3}>
              <Icon as={FiClock} boxSize={10} color="teal.500" />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.600">Tiempo de respuesta inicial</Text>
                <Heading size="lg">{estadisticas.tiempoRespuestaInicial.tiempoPromedio} días</Heading>
                <Text fontSize="xs" color="gray.500">
                  {estadisticas.tiempoRespuestaInicial.expedientesConRespuesta} expedientes analizados
                </Text>
              </VStack>
            </HStack>
          </Box>
        </SimpleGrid>
        
        {/* Resumen de tipos de barreras */}
        <Box mb={6}>
          <Heading size="md" mb={4} color={brandRed}>Distribución por Tipo de Barrera</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {tiposBarreras.map((tipoBarrera, index) => (
              <Box 
                key={index} 
                p={4} 
                bg={
                  tipoBarrera.tipo === 'Urbanística' ? 'blue.50' :
                  tipoBarrera.tipo === 'Edificación' ? 'green.50' :
                  tipoBarrera.tipo === 'Transporte' ? 'red.50' : 'gray.50'
                } 
                borderRadius="lg"
                boxShadow="sm"
              >
                <VStack spacing={2} align="center">
                  <Heading 
                    size="md" 
                    color={
                      tipoBarrera.tipo === 'Urbanística' ? 'blue.500' :
                      tipoBarrera.tipo === 'Edificación' ? 'green.500' :
                      tipoBarrera.tipo === 'Transporte' ? 'red.500' : 'gray.500'
                    }
                  >
                    {tipoBarrera.tipo}
                  </Heading>
                  <Heading size="lg">
                    {tipoBarrera.cantidad}
                  </Heading>
                  <Text fontWeight="medium">
                    {tipoBarrera.porcentaje}% del total
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      </>
    );
  };
  
  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack spacing={8} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Estadísticas de Actividad
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>

          <Box 
            bg="white" 
            p={6} 
            borderRadius="lg" 
            boxShadow="sm"
            mb={6}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Heading color={brandRed} mb={2}>Estadísticas de Actividad</Heading>
                <VStack align="flex-start" spacing={1}>
                  <Text color="gray.600">
                    Análisis de denuncias por semana, día y hora
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {currentUser?.email}
                  </Text>
                </VStack>
              </Box>
              <HStack spacing={4}>
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Volver al panel
                </Button>
                <Button
                  leftIcon={<FiLogOut />}
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => navigate('/login')}
                >
                  Cerrar sesión
                </Button>
              </HStack>
            </Flex>
          </Box>
          
          <Box 
            bg="white" 
            p={6} 
            borderRadius="lg" 
            boxShadow="sm"
            mb={6}
          >
            <Flex justify="space-between" align="center" mb={6}>
              <Heading size="md" color={brandRed}>
                <Icon as={FiBarChart2} mr={2} />
                Estadísticas de Denuncias
              </Heading>
              <HStack>
                <Text>Período:</Text>
                <Select 
                  value={periodoSeleccionado} 
                  onChange={(e) => setPeriodoSeleccionado(Number(e.target.value))}
                  width="150px"
                >
                  <option value={1}>Último mes</option>
                  <option value={3}>Últimos 3 meses</option>
                  <option value={6}>Últimos 6 meses</option>
                  <option value={12}>Último año</option>
                  <option value={24}>Últimos 2 años</option>
                </Select>
              </HStack>
            </Flex>
            
            {cargando ? (
              <Flex justify="center" align="center" height="400px" direction="column">
                <Spinner size="xl" color="red.500" thickness="4px" />
                <Text mt={4} color="gray.600">Cargando estadísticas...</Text>
                <Text mt={2} fontSize="sm" color="gray.500">
                  Esto puede tardar unos momentos
                </Text>
              </Flex>
            ) : error ? (
              <Box p={6} bg="red.50" borderRadius="md" textAlign="center">
                <Heading size="md" color="red.500" mb={2}>
                  Error al cargar las estadísticas
                </Heading>
                <Text color="red.600" mb={4}>{error}</Text>
                <Button 
                  colorScheme="red" 
                  onClick={() => window.location.reload()}
                >
                  Reintentar
                </Button>
              </Box>
            ) : (
              <>
                {estadisticas && (
                  <>
                    <ResumenEstadisticas />
                    
                    {/* Mostrar mensaje si no hay datos */}
                    {estadisticas.porSemana.length === 0 && estadisticas.porDia.length === 0 && (
                      <Box p={6} bg="yellow.50" borderRadius="md" textAlign="center" mb={6}>
                        <Heading size="md" color="yellow.600" mb={2}>
                          No hay datos disponibles
                        </Heading>
                        <Text color="yellow.700">
                          No se encontraron expedientes para el período seleccionado. 
                          Prueba seleccionando un período más amplio o verifica que haya datos en el sistema.
                        </Text>
                      </Box>
                    )}
                  </>
                )}
                
                <Tabs variant="soft-rounded" colorScheme="red" isLazy>
                  <TabList mb={4}>
                    <Tab>Por Semana</Tab>
                    <Tab>Por Día</Tab>
                    <Tab>Por Hora</Tab>
                    <Tab>Usuarios Activos</Tab>
                    <Tab>Tiempo de Respuesta</Tab>
                    <Tab>Tipos de Barreras</Tab>
                    <Tab><Icon as={FiMap} mr={2} />Mapa de Zonas</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <GraficoPorSemana />
                    </TabPanel>
                    <TabPanel>
                      <GraficoPorDia />
                    </TabPanel>
                    <TabPanel>
                      <GraficoPorHora />
                    </TabPanel>
                    <TabPanel>
                      <GraficoUsuariosMasActivos />
                    </TabPanel>
                    <TabPanel>
                      {estadisticas ? (
                        <Box p={4} bg="white" borderRadius="md" boxShadow="sm">
                          <Heading size="md" mb={4}>Tiempo de Respuesta Inicial</Heading>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
                            <Box p={4} bg="teal.50" borderRadius="lg">
                              <VStack align="center" spacing={2}>
                                <Heading size="lg" color="teal.500">
                                  {estadisticas.tiempoRespuestaInicial.tiempoPromedio} días
                                </Heading>
                                <Text fontWeight="medium">Tiempo promedio de respuesta</Text>
                                <Text fontSize="sm" color="gray.600">
                                  Desde la recepción hasta la primera acción
                                </Text>
                              </VStack>
                            </Box>
                            <Box p={4} bg="gray.50" borderRadius="lg">
                              <VStack align="center" spacing={2}>
                                <HStack spacing={4}>
                                  <VStack>
                                    <Heading size="md" color="green.500">
                                      {estadisticas.tiempoRespuestaInicial.expedientesConRespuesta}
                                    </Heading>
                                    <Text fontSize="sm">Con respuesta</Text>
                                  </VStack>
                                  <VStack>
                                    <Heading size="md" color="red.500">
                                      {estadisticas.tiempoRespuestaInicial.expedientesSinRespuesta}
                                    </Heading>
                                    <Text fontSize="sm">Sin respuesta</Text>
                                  </VStack>
                                </HStack>
                                <Text fontSize="sm" color="gray.600" textAlign="center">
                                  Total de expedientes analizados: {
                                    estadisticas.tiempoRespuestaInicial.expedientesConRespuesta + 
                                    estadisticas.tiempoRespuestaInicial.expedientesSinRespuesta
                                  }
                                </Text>
                              </VStack>
                            </Box>
                          </SimpleGrid>
                          <Box mt={4} p={4} bg="blue.50" borderRadius="md">
                            <Text fontSize="sm" color="gray.700">
                              <strong>¿Qué es el tiempo de respuesta inicial?</strong> Es el tiempo promedio que transcurre 
                              desde que se recibe un expediente hasta que se realiza la primera acción o respuesta en el sistema.
                              Este indicador es clave para medir la eficiencia en la gestión inicial de expedientes.
                            </Text>
                          </Box>
                        </Box>
                      ) : (
                        <Text textAlign="center">No hay datos disponibles</Text>
                      )}
                    </TabPanel>
                    <TabPanel>
                      {estadisticas && estadisticas.tiposBarreras ? (
                        <Box p={4} bg="white" borderRadius="md" boxShadow="sm">
                          <Heading size="md" mb={4}>Tipos de Barreras</Heading>
                          
                          {/* Gráfico de distribución de tipos de barreras */}
                          <Box mb={6}>
                            <Heading size="sm" mb={3}>Distribución por Tipo Principal</Heading>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                              {estadisticas.tiposBarreras.map((tipoBarrera, index) => (
                                <Box 
                                  key={index} 
                                  p={4} 
                                  bg={
                                    tipoBarrera.tipo === 'Urbanística' ? 'blue.50' :
                                    tipoBarrera.tipo === 'Edificación' ? 'green.50' :
                                    tipoBarrera.tipo === 'Transporte' ? 'red.50' : 'gray.50'
                                  } 
                                  borderRadius="lg"
                                  boxShadow="sm"
                                >
                                  <VStack spacing={2} align="center">
                                    <Heading 
                                      size="md" 
                                      color={
                                        tipoBarrera.tipo === 'Urbanística' ? 'blue.500' :
                                        tipoBarrera.tipo === 'Edificación' ? 'green.500' :
                                        tipoBarrera.tipo === 'Transporte' ? 'red.500' : 'gray.500'
                                      }
                                    >
                                      {tipoBarrera.tipo}
                                    </Heading>
                                    <Heading size="lg">
                                      {tipoBarrera.cantidad}
                                    </Heading>
                                    <Text fontWeight="medium">
                                      {tipoBarrera.porcentaje}% del total
                                    </Text>
                                  </VStack>
                                </Box>
                              ))}
                            </SimpleGrid>
                          </Box>
                          
                          {/* Detalles por tipo de barrera */}
                          {estadisticas.tiposBarreras.map((tipoBarrera, index) => (
                            tipoBarrera.detalles.length > 0 && (
                              <Box key={index} mt={6}>
                                <Heading size="sm" mb={3}>
                                  Detalles de Barreras de {tipoBarrera.tipo}
                                </Heading>
                                <Box 
                                  p={4} 
                                  bg={
                                    tipoBarrera.tipo === 'Urbanística' ? 'blue.50' :
                                    tipoBarrera.tipo === 'Edificación' ? 'green.50' :
                                    tipoBarrera.tipo === 'Transporte' ? 'red.50' : 'gray.50'
                                  } 
                                  borderRadius="md"
                                >
                                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                    {tipoBarrera.detalles.map((detalle, idx) => (
                                      <Box 
                                        key={idx} 
                                        p={3} 
                                        bg="white" 
                                        borderRadius="md" 
                                        boxShadow="sm"
                                      >
                                        <Text fontWeight="medium">{detalle.tipo}</Text>
                                        <HStack justify="space-between" mt={1}>
                                          <Text>{detalle.cantidad} expedientes</Text>
                                          <Text color="gray.600">{detalle.porcentaje}%</Text>
                                        </HStack>
                                      </Box>
                                    ))}
                                  </SimpleGrid>
                                </Box>
                              </Box>
                            )
                          ))}
                          
                          <Box mt={6} p={4} bg="gray.50" borderRadius="md">
                            <Text fontSize="sm" color="gray.700">
                              <strong>Nota:</strong> Los porcentajes de los detalles se calculan respecto al total de 
                              expedientes de cada tipo principal de barrera. El sistema clasifica las barreras en tres 
                              categorías principales: Urbanísticas, Edificación y Transporte.
                            </Text>
                          </Box>
                        </Box>
                      ) : (
                        <Text textAlign="center">No hay datos disponibles sobre tipos de barreras</Text>
                      )}
                    </TabPanel>
                    <TabPanel>
                      <MapaZonasDenuncias 
                        denuncias={denunciasPorZona} 
                        isLoading={cargandoMapa} 
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
                
                <Box mt={8} p={4} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" color="gray.600">
                    Estas estadísticas muestran la distribución de denuncias a lo largo del tiempo, 
                    permitiendo identificar patrones de actividad y momentos de mayor afluencia.
                  </Text>
                </Box>
              </>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default EstadisticasActividad;
